import api from '@/api'
import Logger from '@/logger'
import router from '@/router'
import { EmptyActiveMappedItemInput, FeatureDoesNotExists, NoActiveClassification, NoActiveFeature } from '../exceptions'

const logger = new Logger('store/valueMapping/actions')
const SELLER_ID = process.env.VUE_APP_NER_CLASSIFICATIONS_SELLER_ID
const DATE = process.env.VUE_APP_NER_CLASSIFICATIONS_DATE

if (!SELLER_ID) throw new Error('Please provide the VUE_APP_NER_CLASSIFICATIONS_SELLER_ID env variable')
if (!DATE) throw new Error('Please provide the VUE_APP_NER_CLASSIFICATIONS_DATE env variable')


/**
 * Get and set the classifications.
 * @param {Object} context
 */
function getClassifications({ commit }) {
  commit('setLoadingClassifications', true)
  return api.classifications.get({ seller: SELLER_ID, date: DATE }).then(classifications => {
    commit('setClassifications', classifications)
    commit('setLoadingClassifications', false)
  }).catch(err => {
    logger.error(err)
    commit('setLoadingClassifications', false)
    throw err
  })
}

/**
 * Get and set the features of the current classification
 * @param {Object} context
 */
function getFeatures({ commit, state }) {
  const classification = state.classification
  if (!classification) throw new NoActiveClassification()
  commit('setLoadingFeatures', true)
  return api.features.get(classification.id).then(features => {
    commit('setFeatures', features)
    commit('setLoadingFeatures', false)
  }).catch(err => {
    logger.error(err);
    commit('setLoadingFeatures', false)
  })
}

/**
 * Remove an item from its associated mapped value in the backend and in the store.
 * The value will need to be mapped again.
 * @param {OBject} context
 * @param {String} value the value to remove associated with the current active feature.
 */
function removeValue({ commit, state }, value) {
  const feature = state.feature
  if (!feature) throw new NoActiveFeature()

  const featureId = feature.id
  commit('setLoadingRemoveValue', true)
  return api.mappedValues.remove({ featureId, value }).then(() => {
    /* Make null the mapped property of the given value */
    commit('setMappedItems', [...state.mappedItems].map(item => {
      if (item.value === value) item.mapped = null
      return item
    }))
    /* Set again the activeMappedItem with the value removed */
    commit('setActiveMappedItem', {
      ...state.activeMappedItem,
      values: state.activeMappedItem.values.filter(item => item.value !== value)
    })
    /* Stop the loading flag and notify the success */
    commit('setLoadingRemoveValue', false)
    commit('notifications/notify', {
      type: 'success',
      message: `"${value}" eliminado con éxito del mapeo "${state.activeMappedItem.mapped}"`
    }, { root: true })
  }).catch(err => {
    commit('setLoadingRemoveValue', false)
    commit('notifications/notify', {
      type: 'error',
      message: 'Lo siento tuvimos un problema eliminando el valor'
    }, { root: true })
    logger.error(err);
  })
}

/**
 * Save the active map between the activeMappedItem and the activeMappedItemInput.
 * @param {Object} context
 */
async function saveActiveMap({ commit, state }) {
  if (!state.activeMappedItemInput) {
    throw new EmptyActiveMappedItemInput()
  }

  const value = state.activeMappedItem[state.showingMappedValues ? 'mapped' : 'value']
  const mapped = state.activeMappedItemInput.toUpperCase().trim()
  const featureId = state.feature.id
  commit('setLoadingSaveActiveMap', true)
  return api.mappedValues.save({ featureId, value, mapped })
    .then(() => {
      /* Update the mapped value of the item */
      const mappedItems = state.mappedItems.map(item => {
        return item[state.showingMappedValues ? 'mapped' : 'value'] === value
          ? { ...item, mapped }
          : item
      })
      commit('setMappedItems', mappedItems)
      commit('notifications/notify', {
        type: 'success',
        message: 'Mapea guardado con éxito'
      }, { root: true })
    })
    .catch(err => {
      logger.error(err)
      commit('notifications/notify', {
        type: 'error',
        message: 'Lo siento tuvimos un problema guardando el mapeo'
      }, { root: true })
    })
    .then(() => commit('setLoadingSaveActiveMap', false))
}

/**
 * Set the active feature by getting it by id from the backend to get its values and commit them.
 * @param {Object} context
 * @param {Number} id the id of the feature that you want to set as active.
 */
function setFeature({ commit, dispatch, state }, id) {
  if (!id) {
    commit('setFeature', null)
    return commit('setMappedItems', null)
  }

  if (id && !state.features.some(f => f.id === id)) {
    throw new FeatureDoesNotExists()
  }

  commit('setFeature', id)
  dispatch('updateFeatureRoute')
  commit('setLoadingMappedItems', true)
  return api.mappedValues.get(id)
    .then(values => commit('setMappedItems', values))
    .catch(err => logger.error(err))
    .then(() => commit('setLoadingMappedItems', false))
}

/**
 * Set the classification route if there is any classification or the home route of the value mapping app.
 * @param {Object} context
 */
function updateClassificationRoute({ state }) {
  if (!state.classification) {
    return router.push({ name: 'valueMapping' })
  }

  const route = router.currentRoute
  const notInCorrectRoute = !route.matched.some(function ({ name }) {
    return name === 'valueMapping/classification'
  })

  if (notInCorrectRoute) {
    return router.push({
      name: 'valueMapping/classification',
      params: {
        ...route.params,
        classificationId: state.classification.id
      }
    })
  }
}

/**
 * Update the route if there is not in the correct one for the active feature.
 * For example, if we have a feature we need to have in the matched routes the route
 * of the feature, we could be in it or in some of it children, in that case this method
 * does nothing.
 * If there is no feature we need to go to the classification route or to the home if there
 * is no classification.
 * @param {Object} state
 */
function updateFeatureRoute({ state, dispatch }) {
  if (!state.feature) {
    return dispatch('setClassificationRoute');
  }

  const route = router.currentRoute
  const notInCorrectRoute = !route.matched.some(function ({ name }) {
    return name === "valueMapping/classification/feature";
  });

  /* Go to the features route */
  if (notInCorrectRoute) {
    return router.push({
      name: "valueMapping/classification/feature",
      params: {
        classificationId: state.classification.id,
        featureId: state.feature.id
      }
    });
  }

  /* Update the current route with the id of the feature */
  const id = Number(route.params.featureId);
  if (id !== state.feature.id) {
    return router.push({
      name: route.name,
      params: {
        ...route.params,
        featureId: state.feature.id
      }
    });
  }
}

export default {
  getClassifications,
  getFeatures,
  removeValue,
  saveActiveMap,
  setFeature,
  updateClassificationRoute,
  updateFeatureRoute
}